import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';

import './index.scss';
import { Navbar, Container, Nav } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectAccessToken, selectIsSuperAdmin } from '../../features/currentUser/currentUserSelectors';
import { logout } from '../../features/currentUser/currentUserSlice';
import { useState, useEffect, useRef } from 'react';
import { cognitoSignOutSafe } from '../../apis/authCognito';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt-BR', label: 'Português (BR)' },
  { value: 'pt-PT', label: 'Português (PT)' },
  { value: 'se', label: 'Svenska' },
];

export const Header = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const token = useAppSelector(selectAccessToken);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const dispatch = useAppDispatch();

  const [isNavOpened, setIsNavOpened] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (!isLangDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLangDropdownOpen]);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
    setIsLangDropdownOpen(false);
  };

  const currentLanguageOption =
    LANGUAGE_OPTIONS.find((opt) => opt.value === currentLang) || LANGUAGE_OPTIONS[0];

  const languageSwitcher = (
    <div className="language-switcher" ref={langDropdownRef}>
      <button
        type="button"
        className="language-switcher__button language-switcher__button--icon"
        onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
        aria-haspopup="menu"
        aria-expanded={isLangDropdownOpen}
        aria-controls="language-switcher-menu"
        aria-label={`Select language (current: ${currentLanguageOption.label})`}
        title={currentLanguageOption.label}
      >
        <svg
          className="language-switcher__globe-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      </button>
      {isLangDropdownOpen && (
        <div id="language-switcher-menu" className="language-switcher__dropdown" role="menu">
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              className={classnames('language-switcher__option', {
                'language-switcher__option--active': option.value === currentLang,
              })}
              onClick={() => handleLanguageChange(option.value)}
              role="menuitemradio"
              aria-checked={option.value === currentLang}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Landing page: only show the language switcher, floating in the top-right.
  if (location.pathname === '/') {
    return <div className="language-switcher--floating">{languageSwitcher}</div>;
  }

  const prefix = 'header-';
  const navigationConfig = [
    {
      title: t(prefix + 'myCommunities'),
      navLink: '/communities',
      auth: true,
    },
    // {
    //     title: t(prefix + 'configuration'),
    //     navLink: '/admin-configuration',
    //     auth: true
    // },
    {
      title: t(prefix + 'admin'),
      navLink: '/admin-tools',
      auth: true,
      superAdmin: true,
    },
    {
      title: t(prefix + 'logout'),
      action: async () => {
        await cognitoSignOutSafe(); // Logout from Cognito
        dispatch(logout()); // Logout from your backend
      },
      auth: true,
    },
    {
      title: t(prefix + 'signIn'),
      navLink: '/',
      auth: false,
    },
    // {
    //     title: t(prefix + 'home'),
    //     navLink: 'admin',
    //     show: false
    // },
    // {
    //     title: t(prefix + 'discover'),
    //     navLink: 'discover',
    //     show: false
    // },
    // {
    //     title: t(prefix + 'adminDashboard'),
    //     navLink: 'adminDashboard',
    //     show: false
    // },
  ].filter((i) => i.auth === !!token && (!('superAdmin' in i) || isSuperAdmin));

  return (
    <div className="navContainer">
      <Navbar
        collapseOnSelect={true}
        expand="lg"
        variant="dark"
        onToggle={setIsNavOpened}
        onSelect={(eventKey) => {
          eventKey && history.push(eventKey);
        }}
      >
        <Container fluid>
          <Navbar.Brand>
            {!isNavOpened && (
              <div
                className="header-logo"
                role="button"
                onContextMenu={(e) => {
                  e.preventDefault();
                }}
              ></div>
            )}
            <img
              className={classnames([
                'raqt-logo d-block mobile-logo',
                {
                  hidden: isNavOpened,
                  /* eslint-disable-next-line */
                  hide: location.pathname === '/',
                },
              ])}
              src={require('../../assets/images/raqt-slogan.png')}
              alt="logo"
              width="70"
              height="39"
              draggable={false}
              style={{ cursor: 'default' }} // Add this line to set the cursor style
            />
          </Navbar.Brand>
          {
            <>
              <Navbar.Toggle aria-controls="responsive-navbar-nav" />
              <Navbar.Collapse id="responsive-navbar-nav">
                <Nav className="d-flex align-items-center">
                  {navigationConfig.map((nav, index) => (
                    <Nav.Link
                      key={index}
                      href={nav.navLink}
                      onClick={(e) => {
                        e.preventDefault();
                        nav.action && nav.action();
                      }}
                    >
                      {nav.title.toLocaleUpperCase()}
                    </Nav.Link>
                  ))}
                  <div className="ms-3">{languageSwitcher}</div>
                </Nav>
                {/* <div className="avatarContainer d-flex justify-content-end">
                                <img
                                    className="rounded-circle"
                                    src={require('../../assets/images/user.png')}
                                    alt="logo"
                                    width="50"
                                    height="50"
                                />
                            </div> */}
              </Navbar.Collapse>
            </>
          }
        </Container>
      </Navbar>
    </div>
  );
};
